import { useEffect, useRef, useState } from 'react';
import { Calculator, Check, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PlatformProductCategory, UnitCode } from '~/graphql/graphql';
import {
  useGetUserPreferences,
  useUpdateProductPreferredUnitFilters,
} from '~/api/auth';
import { Button } from '~/components/ui';
import { isUnitAllowedForCategory } from '~/utils/productUnitPolicy';

type UnitsByCategory = Record<PlatformProductCategory, UnitCode | null>;

const CATEGORY_OPTIONS: { value: PlatformProductCategory; labelKey: string }[] =
  [
    {
      value: PlatformProductCategory.Lumber,
      labelKey: 'user-preferences.unit.category.lumber',
    },
    {
      value: PlatformProductCategory.LinealProducts,
      labelKey: 'user-preferences.unit.category.lineal_products',
    },
    {
      value: PlatformProductCategory.Panels,
      labelKey: 'user-preferences.unit.category.panels',
    },
    {
      value: PlatformProductCategory.Specialty,
      labelKey: 'user-preferences.unit.category.specialty',
    },
  ];

const UNIT_OPTIONS: { value: UnitCode; labelKey: string }[] = [
  { value: UnitCode.Pcs, labelKey: 'user-preferences.unit.pcs.label' },
  { value: UnitCode.Mbf, labelKey: 'user-preferences.unit.mbf.label' },
  { value: UnitCode.Bf, labelKey: 'user-preferences.unit.bf.label' },
  { value: UnitCode.Lf, labelKey: 'user-preferences.unit.lf.label' },
  { value: UnitCode.Msf, labelKey: 'user-preferences.unit.msf.label' },
  { value: UnitCode.Sf, labelKey: 'user-preferences.unit.sf.label' },
  { value: UnitCode.Cbm, labelKey: 'user-preferences.unit.cbm.label' },
];

const DEFAULT_UNITS: UnitsByCategory = {
  [PlatformProductCategory.Lumber]: null,
  [PlatformProductCategory.LinealProducts]: null,
  [PlatformProductCategory.Panels]: null,
  [PlatformProductCategory.Specialty]: null,
};

export function UnitPreferences() {
  const { t } = useTranslation('user-preferences');

  const [unitsByCategory, setUnitsByCategory] =
    useState<UnitsByCategory>(DEFAULT_UNITS);

  const { data: prefsData, isLoading } = useGetUserPreferences();
  const { mutate: saveFilters, isPending: isSaving } =
    useUpdateProductPreferredUnitFilters();

  // Only sync server state into local state on first load. Without this guard
  // every background refetch (staleTime = 30s) would overwrite any in-progress
  // selections the user has made but not yet saved.
  // Using useRef (not useState) so flipping the flag does not trigger a re-render,
  // which would cause the effect to run a second time before React can bail out.
  const prefsInitialized = useRef(false);
  useEffect(() => {
    if (prefsInitialized.current) return;
    const saved = prefsData?.userPreferences?.productPreferredUnitFilters;
    // Mark initialized on any non-null response (even empty preferences) so
    // subsequent background refetches never overwrite in-progress user edits.
    if (prefsData?.userPreferences != null) prefsInitialized.current = true;
    if (!saved?.length) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync from server on first load only; prefsInitialized ref prevents cascading re-runs
    setUnitsByCategory((prev) => {
      const next = { ...prev };
      for (const entry of saved) {
        next[entry.category] = isUnitAllowedForCategory(
          entry.category,
          entry.unit
        )
          ? entry.unit
          : null;
      }
      return next;
    });
  }, [prefsData]);

  function handleUnitChange(category: PlatformProductCategory, unit: UnitCode) {
    setUnitsByCategory((prev) => ({ ...prev, [category]: unit }));
  }

  function handleSave() {
    saveFilters(
      {
        filters: CATEGORY_OPTIONS.filter(
          ({ value: category }) => unitsByCategory[category] != null
        ).map(({ value: category }) => ({
          category,
          unit: unitsByCategory[category] as UnitCode,
        })),
      },
      {
        onSuccess: () => toast.success(t('user-preferences.unit.save_success')),
        onError: () => toast.error(t('user-preferences.unit.save_error')),
      }
    );
  }

  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-prim-100 rounded-lg">
          <Calculator size={20} className="text-prim-600" />
        </div>
        <h2 className="text-neut-prim font-semibold">
          {t('user-preferences.unit.title')}
        </h2>
      </div>

      <p className="text-sm text-neut-sec mb-4">
        {t('user-preferences.unit.preferred_unit.description')}
      </p>

      <div className="space-y-4">
        {CATEGORY_OPTIONS.map(({ value: category, labelKey }) => {
          const allowedOptions = UNIT_OPTIONS.filter((opt) =>
            isUnitAllowedForCategory(category, opt.value)
          );
          // A category with exactly one allowed unit has no real choice to make — show it as
          // already selected rather than an unmade decision the backend overrides anyway.
          const isOnlyOption = allowedOptions.length === 1;
          return (
            <div key={category}>
              <p className="text-[10px] font-semibold text-neut-500 uppercase tracking-wide mb-2">
                {t(labelKey)}
              </p>
              <div
                className="flex flex-wrap gap-2"
                id={`unit-selector-${category}`}
              >
                {allowedOptions.map((opt) => {
                  const isSelected =
                    isOnlyOption || unitsByCategory[category] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={isLoading}
                      onClick={() => handleUnitChange(category, opt.value)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? 'bg-prim-50 border-prim-300 text-prim-700 font-medium'
                          : 'bg-neut-bg-alt border-neut-border text-neut-600 hover:border-neut-300'
                      }`}
                    >
                      {isSelected && (
                        <Check size={10} className="inline mr-1" />
                      )}
                      {t(opt.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-xs text-neut-sec mb-5">
        <p className="font-medium mb-1">
          {t('user-preferences.unit.descriptions.title')}
        </p>
        <ul className="space-y-1 ml-4 list-disc">
          <li>
            <strong>PCS</strong> – {t('user-preferences.unit.pcs.desc')}
          </li>
          <li>
            <strong>MBF</strong> – {t('user-preferences.unit.mbf.desc')}
          </li>
          <li>
            <strong>BF</strong> – {t('user-preferences.unit.bf.desc')}
          </li>
          <li>
            <strong>LF</strong> – {t('user-preferences.unit.lf.desc')}
          </li>
          <li>
            <strong>MSF</strong> – {t('user-preferences.unit.msf.desc')}
          </li>
          <li>
            <strong>SF</strong> – {t('user-preferences.unit.sf.desc')}
          </li>
          <li>
            <strong>CBM</strong> – {t('user-preferences.unit.cbm.desc')}
          </li>
        </ul>
      </div>

      <div className="flex justify-end">
        <Button
          width="auto"
          size="sm"
          onClick={handleSave}
          loading={isSaving}
          disabled={isLoading}
        >
          <Save size={16} />
          {t('user-preferences.unit.save')}
        </Button>
      </div>
    </div>
  );
}
